import { AvatarChip } from "components/designV2/avatarChip/avatarChip";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { SidePanel } from "components/designV2/sidePanel/sidePanel";
import { TextArea } from "components/designV2/textarea/textarea";
import { TimeReportDto } from "model/TimeReport";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Api } from "services/api/api.service";
import { useAlertStore } from "stores/alertStore/alertStore";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import {
  formatDateWithWeekdayLabel,
  formatMinutesDuration,
  formatTimeRangeLabel,
  mergeIsoDateWithTimeInput,
  minutesBetweenTimeInputs,
  signedMinutesBetweenIso,
  toTimeInputValue,
} from "web-apps/company/utils/timeAndDate";
import { DetailsRow } from "web-apps/company/components/timeReportsV2/components/views/person/components/detailsPanel/detailsRow.component";
import { EditableRangeRow } from "web-apps/company/components/timeReportsV2/components/views/person/components/detailsPanel/editableRangeRow.component";
import { TotalTimeRow } from "web-apps/company/components/timeReportsV2/components/views/person/components/detailsPanel/totalTimeRow.component";
import {
  DataCard,
  Section,
  SectionTitle,
} from "web-apps/company/components/timeReportsV2/components/views/person/components/detailsPanel/timeReportDetailsPanel.styled";
import {
  resolveCanEditTime,
  resolveClockStampThresholds,
} from "./clockStampTimeReportPanel.logic";

const PANEL_TITLE_ID = "clock-stamp-time-report-panel-title";

interface Props {
  isOpen: boolean;
  onBackdropClick?: () => void;
  onClose: () => void;
  onSubmitted: () => void;
  timeReport: TimeReportDto;
  phoneNumber: string;
}

interface BreakPayload {
  breakTime: string | undefined;
  breakStartTime: string | undefined;
  breakEndTime: string | undefined;
}

/**
 * Picks planned vs stamped time as the initial reported value: prefers planned
 * when the stamp is within the configured before/after thresholds, otherwise
 * uses the stamp.
 */
const resolveReportedTimeSeed = (
  plannedIso: string,
  stampedIso: string | null | undefined,
  beforeThresholdMinutes: number,
  afterThresholdMinutes: number,
): string => {
  if (!stampedIso) return plannedIso;
  const minutes = signedMinutesBetweenIso(stampedIso, plannedIso);
  if (minutes == null) return stampedIso;
  if (minutes < 0 && Math.abs(minutes) <= beforeThresholdMinutes)
    return plannedIso;
  if (minutes > 0 && minutes <= afterThresholdMinutes) return plannedIso;
  if (minutes === 0) return plannedIso;
  return stampedIso;
};

export const ClockStampTimeReportPanel = ({
  isOpen,
  onBackdropClick,
  onClose,
  onSubmitted,
  timeReport,
  phoneNumber,
}: Props): JSX.Element => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [companyState] = useCompanyStore();
  const [, , alertDispatch] = useAlertStore();
  const [loading, setLoading] = useState(false);
  const [userNotes, setUserNotes] = useState("");

  const thresholds = resolveClockStampThresholds(companyState.companySettings);
  const canEditTime = resolveCanEditTime(companyState.companySettings);

  const [reportedStart, setReportedStart] = useState(() =>
    toTimeInputValue(
      resolveReportedTimeSeed(
        timeReport.startTime,
        timeReport.checkInTime,
        thresholds.beforeStartMinutes,
        thresholds.afterStartMinutes,
      ),
    ),
  );
  const [reportedEnd, setReportedEnd] = useState(() =>
    toTimeInputValue(
      resolveReportedTimeSeed(
        timeReport.endTime,
        timeReport.checkOutTime,
        thresholds.beforeEndMinutes,
        thresholds.afterEndMinutes,
      ),
    ),
  );
  const [editedBreakStart, setEditedBreakStart] = useState(() =>
    toTimeInputValue(timeReport.breakStartTime),
  );
  const [editedBreakEnd, setEditedBreakEnd] = useState(() =>
    toTimeInputValue(timeReport.breakEndTime),
  );

  const workerName = `${timeReport.firstName ?? ""} ${
    timeReport.lastName ?? ""
  }`.trim();

  const plannedStart =
    timeReport.companyGigShift?.startTime ?? timeReport.startTime;
  const plannedEnd = timeReport.companyGigShift?.endTime ?? timeReport.endTime;

  const breakMinutes = canEditTime
    ? minutesBetweenTimeInputs(editedBreakStart, editedBreakEnd)
    : Number(timeReport.breakTime) || 0;
  const reportedMinutes = minutesBetweenTimeInputs(reportedStart, reportedEnd);
  const totalMinutes = Math.max(0, reportedMinutes - breakMinutes);

  const breakDisplay =
    breakMinutes > 0
      ? timeReport.breakStartTime && timeReport.breakEndTime
        ? t("TimeReports.BreakWithRange", {
            duration: formatMinutesDuration(breakMinutes),
            range: formatTimeRangeLabel(
              timeReport.breakStartTime,
              timeReport.breakEndTime,
              locale,
            ),
          })
        : formatMinutesDuration(breakMinutes)
      : "0h";

  const buildBreakPayload = (): BreakPayload | null => {
    if (!canEditTime) {
      return {
        breakTime: timeReport.breakTime,
        breakStartTime: timeReport.breakStartTime,
        breakEndTime: timeReport.breakEndTime,
      };
    }
    const breakStartEmpty = editedBreakStart.trim() === "";
    const breakEndEmpty = editedBreakEnd.trim() === "";
    if (breakStartEmpty && breakEndEmpty) {
      return {
        breakTime: "0",
        breakStartTime: undefined,
        breakEndTime: undefined,
      };
    }
    if (breakStartEmpty || breakEndEmpty) return null;

    const mergedBreakStart = mergeIsoDateWithTimeInput(
      timeReport.breakStartTime ?? timeReport.startTime,
      editedBreakStart,
    );
    const mergedBreakEnd = mergeIsoDateWithTimeInput(
      timeReport.breakEndTime ?? timeReport.endTime,
      editedBreakEnd,
    );
    if (!mergedBreakStart || !mergedBreakEnd) return null;

    return {
      breakTime: String(
        minutesBetweenTimeInputs(editedBreakStart, editedBreakEnd),
      ),
      breakStartTime: mergedBreakStart,
      breakEndTime: mergedBreakEnd,
    };
  };

  const handleSubmit = (): void => {
    if (
      !timeReport.id ||
      !companyState.company?.id ||
      loading ||
      !phoneNumber
    ) {
      return;
    }

    const startTime = mergeIsoDateWithTimeInput(
      timeReport.startTime,
      reportedStart,
    );
    const endTime = mergeIsoDateWithTimeInput(
      timeReport.endTime,
      reportedEnd,
    );
    if (!startTime || !endTime) return;

    const breakPayload = buildBreakPayload();
    if (!breakPayload) return;

    const cleanedPhoneNumber = phoneNumber.startsWith("+46")
      ? phoneNumber.substring(1)
      : phoneNumber;

    const data = {
      startTime,
      endTime,
      userNotes,
      ...breakPayload,
    };

    setLoading(true);
    Api()
      .company.clockStampTimeReport(
        companyState.company.id,
        cleanedPhoneNumber,
        timeReport.id,
        data,
      )
      .then(() => {
        setLoading(false);
        alertDispatch({
          type: "SHOW_ALERT",
          payload: {
            title: t("WorkerTimeReport.TimeReported"),
            message: "",
          },
        });
        onSubmitted();
      })
      .catch(() => {
        setLoading(false);
        alertDispatch({
          type: "SHOW_ALERT",
          payload: {
            icon: "alert",
            title: t("Alerts.OhNo"),
            message: t("RegisterCompany.SomethingWentWrong"),
          },
        });
      });
  };

  const footer = (
    <FooterActions>
      <SecondaryButton
        label={t("WorkerTimeReport.Cancel")}
        size="small"
        onClick={onClose}
      />
      <PrimaryButton
        label={t("WorkerTimeReport.Submit")}
        size="small"
        disabled={loading}
        onClick={handleSubmit}
      />
    </FooterActions>
  );

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      onBackdropClick={onBackdropClick}
      title={t("TimeReports.TimeReport")}
      titleId={PANEL_TITLE_ID}
      footer={footer}
    >
      <AvatarChip
        title={workerName}
        subTitle={timeReport.gigRole}
      />
      <Section>
        <SectionTitle>{t("WorkerTimeReport.ReportTime")}</SectionTitle>
        <DataCard>
          <DetailsRow
            label={t("TimeReports.Date")}
            value={formatDateWithWeekdayLabel(timeReport.startTime, locale)}
          />
          {canEditTime ? (
            <EditableRangeRow
              label={t("WorkerTimeReport.Report")}
              fromLabel={t("General.From")}
              toLabel={t("General.To")}
              startValue={reportedStart}
              endValue={reportedEnd}
              onStartChange={setReportedStart}
              onEndChange={setReportedEnd}
            />
          ) : (
            <DetailsRow
              label={t("WorkerTimeReport.Report")}
              value={`${reportedStart} - ${reportedEnd}`}
            />
          )}
          <DetailsRow
            label={t("TimeReports.Stamped")}
            value={formatTimeRangeLabel(
              timeReport.checkInTime,
              timeReport.checkOutTime,
              locale,
            )}
          />
          <DetailsRow
            label={t("TimeReports.Planned")}
            value={formatTimeRangeLabel(plannedStart, plannedEnd, locale)}
          />
          <TotalTimeRow
            label={t("TimeReports.TotalTime")}
            totalValue={formatMinutesDuration(totalMinutes)}
            overtimeLabel={null}
          />
          {canEditTime ? (
            <EditableRangeRow
              label={t("TimeReports.Break")}
              fromLabel={t("General.From")}
              toLabel={t("General.To")}
              startValue={editedBreakStart}
              endValue={editedBreakEnd}
              onStartChange={setEditedBreakStart}
              onEndChange={setEditedBreakEnd}
            />
          ) : (
            <DetailsRow label={t("TimeReports.Break")} value={breakDisplay} />
          )}
        </DataCard>
      </Section>
      <TextArea
        label={t("WorkerTimeReport.Notes")}
        value={userNotes}
        onChange={setUserNotes}
        placeholder={t("WorkerTimeReport.NotesPlaceHolder")}
        maxLength={500}
        maxHeight={240}
      />
    </SidePanel>
  );
};

const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;
